import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Slider from 'react-slick';
import { motion } from 'framer-motion';
import {
  FaBookOpen, FaEye, FaBullseye, FaBalanceScale,
  FaRocket, FaLightbulb, FaFacebookF, FaTwitter, FaLinkedinIn, FaInstagram
} from 'react-icons/fa';
import { Quote, Star } from 'lucide-react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

// Tech Stack Icons (Keeping existing imports)
import {
  SiHtml5, SiCss3, SiSass, SiReact, SiNextdotjs, SiJavascript, SiTypescript,
  SiNodedotjs, SiExpress, SiMongodb, SiFirebase, SiGraphql, SiMysql,
  SiRedux, SiVite, SiPostman, SiJenkins, SiDjango, SiGoland, SiAdobephotoshop,
  SiAdobeillustrator, SiAdobexd, SiAdobeaftereffects, SiAdobepremierepro,
  SiFlutter, SiSalesforce, SiWix, SiYii, SiLaravel, SiCodeigniter,
  SiGoogleads, SiTensorflow, SiPytorch
} from 'react-icons/si';

import {
  FaReact, FaNodeJs, FaPhp, FaPython, FaDocker, FaAws,
  FaGitAlt, FaFigma, FaJava, FaVideo, FaRobot, FaWordpress
} from 'react-icons/fa';

const AboutPage = () => {
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4100";

  // State
  const [aboutData, setAboutData] = useState({});
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [flippedIndex, setFlippedIndex] = useState(null);
  const [activeCategory, setActiveCategory] = useState("Frontend & Mobile");

  const cardRefs = useRef([]);

  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
    fetchAboutData();
    fetchTestimonials();

    // Click outside listener for flip cards
    const handleClickOutside = (event) => {
      if (
        cardRefs.current.length &&
        !cardRefs.current.some(ref => ref && ref.contains(event.target))
      ) {
        setFlippedIndex(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch About Data (Story, Vision, etc.)
  const fetchAboutData = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/about/get`);
      if (Array.isArray(res.data)) {
        const mapped = {};
        res.data.forEach((item) => {
          if (item.status) {
            mapped[item.title.toLowerCase()] = item.content;
          }
        });
        setAboutData(mapped);
      }
    } catch (err) {
      console.error("Error fetching about data:", err);
    }
  };

  // Fetch Testimonials
  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/admin/public/testimonials`);
      const data = res.data.testimonials || res.data;
      if (Array.isArray(data)) {
        const active = data.filter(t => t.status === true);
        setTestimonials(active);
      }
    } catch (err) {
      console.error("Error fetching testimonials:", err);
    } finally {
      setLoading(false);
    }
  };

  // Flip Card Data Configuration
  const flipCards = [
    {
      title: 'Story',
      icon: <FaBookOpen size={50} />,
      content: aboutData.story || "Loading our story..."
    },
    {
      title: 'Vision',
      icon: <FaEye size={50} />,
      content: aboutData.vision || "Loading our vision..."
    },
    {
      title: 'Mission',
      icon: <FaBullseye size={50} />,
      content: aboutData.mission || "Loading our mission..."
    },
    {
      title: 'Values',
      icon: <FaBalanceScale size={50} />,
      content: aboutData.values || "Loading our values..."
    }
  ];

  // Tech Stack Configuration (Kept same as original)
  const techCategories = [
    {
      name: "Frontend & Mobile",
      items: [
        { icon: <FaReact />, label: "ReactJS" },
        { icon: <FaReact />, label: "React Native" },
        { icon: <SiTypescript />, label: "TypeScript" },
        { icon: <SiJavascript />, label: "JavaScript" },
        { icon: <SiNextdotjs />, label: "Next.js" },
        { icon: <SiFlutter />, label: "Flutter" },
      ],
    },
    {
      name: "Backend & Database",
      items: [
        { icon: <SiMysql />, label: "MySQL" },
        { icon: <SiMongodb />, label: "MongoDB" },
        { icon: <FaNodeJs />, label: "Node.js" },
        { icon: <FaPhp />, label: "PHP" },
        { icon: <SiLaravel />, label: "Laravel" },
        { icon: <FaPython />, label: "Python" },
        { icon: <SiDjango />, label: "Django" },
      ],
    },
    {
      name: "Design & Creative",
      items: [
        { icon: <SiAdobeillustrator />, label: "Illustrator" },
        { icon: <SiAdobephotoshop />, label: "Photoshop" },
        { icon: <FaFigma />, label: "Figma" },
        { icon: <SiAdobeaftereffects />, label: "After Effects" },
      ],
    },
    {
      name: "Marketing & AI",
      items: [
        { icon: <SiGoogleads />, label: "Google Ads" },
        { icon: <FaRobot />, label: "AI Solutions" },
        { icon: <SiTensorflow />, label: "ML Tools" },
      ],
    }
  ];

  // Slider Settings
  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 800,
    slidesToShow: 2,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    arrows: false,
    responsive: [
      {
        breakpoint: 992,
        settings: { slidesToShow: 1 }
      }
    ]
  };

  return (
    <div className="font-sans text-slate-800 bg-slate-50 min-h-screen pb-20 overflow-x-hidden">

      {/* HERO SECTION */}
      <section className="relative bg-slate-900 pt-32 pb-48 text-center px-6 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10"></div>
        <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] bg-indigo-500/20 rounded-full blur-[120px]"></div>
        <div className="absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] bg-pink-500/20 rounded-full blur-[120px]"></div>

        <div className="relative z-10 max-w-4xl mx-auto" data-aos="fade-up">
          <span className="inline-block py-1 px-3 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 font-bold text-xs mb-6 uppercase tracking-widest">
            Who We Are
          </span>
          <h1 className="text-5xl md:text-7xl font-black text-white mb-8 leading-tight">
            Turning Ideas Into <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-cyan-400">Digital Reality</span>
          </h1>
          <p className="text-xl text-slate-400 font-light leading-relaxed">
            At Infinex, we don't just write code. We build the future. We believe in turning bold ideas into scalable digital experiences that matter.
          </p>
        </div>
      </section>

      {/* INTRO GRID */}
      <section className="container mx-auto px-6 -mt-24 relative z-20 mb-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

          {/* Image */}
          <div className="relative group" data-aos="fade-right">
            <div className="absolute inset-0 bg-indigo-600 rounded-3xl transform rotate-3 group-hover:rotate-1 transition-transform duration-500"></div>
            <img
              src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80"
              alt="Team"
              className="relative rounded-3xl shadow-2xl w-full h-[400px] object-cover transform -rotate-3 group-hover:rotate-0 transition-transform duration-500 border-4 border-white"
            />
          </div>

          {/* Text */}
          <div className="bg-white p-10 rounded-3xl shadow-xl shadow-slate-200/50" data-aos="fade-left">
            <div className="w-16 h-16 bg-indigo-50 rounded-2xl flex items-center justify-center text-indigo-600 mb-6">
              <FaRocket size={32} />
            </div>
            <h2 className="text-3xl font-bold mb-6 text-slate-800">Technology Driving Tomorrow</h2>
            <p className="text-slate-600 leading-relaxed mb-6 font-light text-lg">
              At Infinex Technology, we transform complex digital challenges into streamlined solutions that drive growth, efficiency, and innovation for businesses ready to thrive in the digital age.
            </p>
            <div className="grid grid-cols-2 gap-6">
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                <FaLightbulb className="text-amber-500 mb-3" size={24} />
                <h4 className="font-bold text-slate-800 mb-1">Our Purpose</h4>
                <p className="text-sm text-slate-500">To bridge the gap between ambitious ideas and reality.</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                <FaRocket className="text-rose-500 mb-3" size={24} />
                <h4 className="font-bold text-slate-800 mb-1">Our Ambition</h4>
                <p className="text-sm text-slate-500">Accelerating growth through cutting-edge tech.</p>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* FLIP CARDS SECTION (Story, Vision, Mission, Values) */}
      <section className="container mx-auto px-6 mb-32" data-aos="fade-up">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-extrabold text-slate-800 mb-4">Our Core Philosophy</h2>
          <div className="h-1.5 w-24 bg-gradient-to-r from-indigo-500 to-cyan-500 mx-auto rounded-full"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {flipCards.map((card, index) => (
            <div
              key={index}
              className={`relative h-[320px] cursor-pointer group perspective-1000`}
              onClick={() => setFlippedIndex(flippedIndex === index ? null : index)}
              ref={el => cardRefs.current[index] = el}
            >
              <motion.div
                className="w-full h-full relative transition-all duration-700 preserve-3d shadow-xl rounded-3xl"
                animate={{ rotateY: flippedIndex === index ? 180 : 0 }}
              >
                {/* Front */}
                <div className="absolute inset-0 backface-hidden bg-white rounded-3xl p-8 flex flex-col items-center justify-center text-center border border-slate-100 group-hover:border-indigo-100 group-hover:shadow-2xl transition-all">
                  <div className="text-indigo-600 mb-6 bg-indigo-50 p-6 rounded-full">{card.icon}</div>
                  <h3 className="text-2xl font-bold text-slate-800 mb-2">{card.title}</h3>
                  <span className="text-xs font-bold text-indigo-400 uppercase tracking-widest mt-auto border border-indigo-100 px-4 py-2 rounded-full group-hover:bg-indigo-600 group-hover:text-white transition-colors">Click to Read</span>
                </div>

                {/* Back */}
                <div className="absolute inset-0 backface-hidden bg-gradient-to-br from-indigo-600 to-blue-700 rounded-3xl p-8 flex flex-col items-center justify-center text-center text-white rotate-y-180">
                  <h3 className="text-xl font-bold mb-4 border-b border-indigo-400 pb-2 w-full">{card.title}</h3>
                  <div className="overflow-y-auto max-h-[200px] scrollbar-thin scrollbar-white">
                    <p className="text-indigo-50 leading-relaxed text-sm">{card.content}</p>
                  </div>
                </div>
              </motion.div>
            </div>
          ))}
        </div>
      </section>

      {/* TECH STACK SECTION */}
      <section className="py-24 bg-slate-900 relative overflow-hidden mb-32">
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-5"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Technologies We Master</h2>

            {/* Tabs */}
            <div className="flex flex-wrap justify-center gap-3">
              {techCategories.map((cat, i) => (
                <button
                  key={i}
                  onClick={() => setActiveCategory(cat.name)}
                  className={`px-6 py-2.5 rounded-full text-sm font-bold transition-all ${activeCategory === cat.name
                    ? "bg-indigo-500 text-white shadow-lg shadow-indigo-500/30 scale-105"
                    : "bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white"
                    }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>

          {/* Grid */}
          <div className="flex flex-wrap justify-center gap-6" data-aos="fade-up">
            {techCategories.find(c => c.name === activeCategory)?.items.map((tech, i) => (
              <div key={i} className="flex flex-col items-center gap-3 bg-slate-800/50 p-6 rounded-2xl border border-slate-700 hover:border-indigo-500/50 hover:bg-slate-800 transition-all w-32 h-32 justify-center group">
                <div className="text-4xl text-slate-400 group-hover:text-white transition-colors group-hover:scale-110 duration-300">
                  {tech.icon}
                </div>
                <span className="text-xs font-semibold text-slate-500 group-hover:text-indigo-300">{tech.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DYNAMIC TESTIMONIALS SLIDER */}
      <section className="container mx-auto px-6 mb-32" data-aos="fade-up">
        <div className="text-center mb-16">
          <h5 className="text-indigo-600 font-bold uppercase tracking-widest text-xs mb-2">Client Feedback</h5>
          <h2 className="text-4xl font-extrabold text-slate-800">What They Are Saying</h2>
        </div>

        {loading ? (
          <div className="text-center p-12 text-slate-400 font-semibold">Loading testimonials...</div>
        ) : testimonials.length === 0 ? (
          <div className="text-center bg-white p-12 rounded-3xl shadow-sm border border-slate-100 text-slate-500">
            No testimonials available yet.
          </div>
        ) : (
          <div className="max-w-6xl mx-auto px-4">
            <Slider {...sliderSettings}>
              {testimonials.map((item, index) => (
                <div key={index} className="px-4 py-4">
                  <div className="bg-white p-8 rounded-[2rem] shadow-xl shadow-slate-200/50 border border-slate-100 h-full min-h-[300px] flex flex-col relative">
                    <Quote className="text-indigo-100 absolute top-8 right-8" size={48} />

                    {/* Profile */}
                    <div className="flex items-center gap-4 mb-6 relative z-10">
                      <div className="w-16 h-16 rounded-full bg-slate-100 border-2 border-white shadow-md overflow-hidden flex-shrink-0">
                        {item.image ? (
                          <img src={`${API_URL}/uploads/${item.image}`} alt={item.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-indigo-50 text-indigo-500 font-bold text-xl">{item.name?.charAt(0)}</div>
                        )}
                      </div>
                      <div>
                        <h4 className="font-bold text-lg text-slate-900">{item.name}</h4>
                        <div className="flex text-yellow-400 gap-0.5 text-xs mt-1">
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} size={12} fill={i < item.rating ? "currentColor" : "none"} className={i < item.rating ? "" : "text-slate-200"} />
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Content */}
                    <p className="text-slate-600 leading-relaxed italic flex-grow relative z-10">"{item.review}"</p>
                  </div>
                </div>
              ))}
            </Slider>
          </div>
        )}
      </section>



    </div>
  );
};

export default AboutPage;
